/**
 * ConstructionCsvDownload Component
 * 
 * Component for downloading construction worker salary data as CSV files.
 * Provides filtering options to download specific data sets based on:
 * - Data type toggle (給与 / 賞与)
 * - Year, month (給与) or period (賞与), and site (現場)

 * 
 * Features:
 * - Year and month selection (dynamically loaded from backend)
 * - Site (現場) selection (dynamically loaded from backend)
 * - At least one filter required for download
 * - Data preview (fetched based on selected filters)
 * - Loading state management
 * - Success/error message display with auto-dismiss
 * - CSV file download with proper Japanese encoding (UTF-8 BOM)
 * - CSV format matching sample files structure
 * - Automatic filename generation based on filters
 * 
 * Note: Construction workers use different fields than office employees:
 * - Site (現場/genbaName) instead of department
 * - 賞与 uses construction-bonus APIs and the 15-column source CSV

 * - Employee name instead of employee code
 * 
 * Used by:
 * - SalaryDataMain component for construction worker data download
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import '../../css/CsvDownload.css';
import { API_BASE_URL } from '../../../../config/apiConfig';
import { authenticatedFetch } from '../../../../utils/authenticatedFetch';
import {
  CONSTRUCTION_BONUS_API_BASE_PATH,
  constructionBonusDownloadFilename,
  constructionBonusRowsToCsv,
  ConstructionBonusDataRow,
} from '../../../../utils/constructionBonusContract';

/**
 * ConstructionCsvDownload Component
 * 
 * Main component for downloading construction worker salary CSV files.
 * Manages filter state, preview data, and download operations.
 * 
 * @returns React functional component
 */
export const ConstructionCsvDownload = () => {
  /**
   * State management:
   * - selectedYear: Selected year filter (empty string = not selected)
   * - selectedMonth: Selected month filter (empty string = not selected)
   * - selectedGenba: Selected site (現場) filter (empty string = all sites)
   * - exportFormat: Export format ('csv' - currently only CSV supported)
   * - includeHeaders: Whether to include CSV headers (currently always true)
   * - isDownloading: Loading state for download operation
   * - downloadMessage: Success or error message to display
   * - availableYears: Array of available years from backend
   * - availableMonths: Array of available months from backend
   * - availableGenbas: Array of available sites (現場) from backend
   * - previewData: Array of data records for preview/download (fetched based on filters)
   */
  const [selectedDataType, setSelectedDataType] = useState<'salary' | 'bonus'>('salary');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [selectedGenba, setSelectedGenba] = useState('');
  const [exportFormat, setExportFormat] = useState('csv');
  const [includeHeaders, setIncludeHeaders] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadMessage, setDownloadMessage] = useState<string>('');
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [availableMonths, setAvailableMonths] = useState<number[]>([]);
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([]);
  const [availableGenbas, setAvailableGenbas] = useState<string[]>([]);
  const [previewData, setPreviewData] = useState<any[]>([]);

  /**
   * Loads available filter options from the API.
   * 
   * Fetches available years, months, and sites (現場) from backend
   * to populate filter dropdowns. Uses Promise.all for parallel requests.
   * 
   * Endpoints:
   * - /construction-salary/years - Available years
   * - /construction-salary/months - Available months
   * - /construction-salary/genbas - Available sites (現場)
   * 
   * @example
   * ```typescript
   * await loadFilterOptions();
   * // Populates availableYears, availableMonths, availableGenbas
   * ```
   */
  const apiBase = selectedDataType === 'bonus'
    ? `${API_BASE_URL}${CONSTRUCTION_BONUS_API_BASE_PATH}`
    : `${API_BASE_URL}/construction-salary`;

  const loadFilterOptions = async () => {
    try {
      if (selectedDataType === 'bonus') {
        const [yearsResponse, genbasResponse] = await Promise.all([
          authenticatedFetch(`${apiBase}/years`),
          authenticatedFetch(`${apiBase}/genbas`)
        ]);
        const yearsResult = await yearsResponse.json();
        const genbasResult = await genbasResponse.json();
        if (yearsResult.success) setAvailableYears(yearsResult.years || []);
        if (genbasResult.success) setAvailableGenbas(genbasResult.genbas || []);
        setAvailableMonths([]);
        try {
          const periodsResponse = await authenticatedFetch(`${apiBase}/periods`);
          const periodsResult = await periodsResponse.json();
          const periods: string[] = periodsResult.success ? (periodsResult.periods || []) : [];
          setAvailablePeriods(
            [...periods].sort((a, b) => Number(a) - Number(b) || String(a).localeCompare(String(b)))
          );
        } catch {
          setAvailablePeriods([]);
        }
        return;
      }

      const [yearsResponse, monthsResponse, genbasResponse] = await Promise.all([
        authenticatedFetch(`${apiBase}/years`),
        authenticatedFetch(`${apiBase}/months`),
        authenticatedFetch(`${apiBase}/genbas`)
      ]);

      const yearsResult = await yearsResponse.json();
      const monthsResult = await monthsResponse.json();
      const genbasResult = await genbasResponse.json();

      if (yearsResult.success) setAvailableYears(yearsResult.years);
      if (monthsResult.success) setAvailableMonths(monthsResult.months);
      if (genbasResult.success) setAvailableGenbas(genbasResult.genbas);
      setAvailablePeriods([]);
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  /**
   * Loads preview data based on selected filters.
   * 
   * Fetches construction worker data from backend based on current filter selections.
   * Used to preview data before download and to generate CSV file.
   * 
   * Filter parameters:
   * - genbaName: Filter by site (現場) if selected
   * - year: Filter by year if selected
   * - month: Filter by month if selected
   * 
   * Endpoints:
   * - No filters: /construction-salary/data/all (all records)
   * - With filters: /construction-salary/data?params (filtered records)
   * 
   * @example
   * ```typescript
   * // Load all data
   * selectedGenba = '';
   * selectedYear = '';
   * selectedMonth = '';
   * await loadPreviewData();
   * 
   * // Load filtered data
   * selectedGenba = '現場A';
   * selectedYear = '2024';
   * selectedMonth = '1';
   * await loadPreviewData();
   * ```
   */
  const loadPreviewData = async () => {
    try {
      let url = `${apiBase}/data/all`;

      const params = new URLSearchParams();
      if (selectedGenba) params.append('genbaName', selectedGenba);
      if (selectedYear) params.append('year', selectedYear);
      if (selectedDataType === 'bonus') {
        if (selectedPeriod) params.append('period', selectedPeriod);
      } else if (selectedMonth) {
        params.append('month', selectedMonth);
      }

      if (params.toString()) {
        url = `${apiBase}/data?${params.toString()}`;
      }

      const response = await authenticatedFetch(url);
      const result = await response.json();

      if (result.success) {
        setPreviewData(result.data || []);
      }
    } catch (error) {
      console.error('Error loading preview data:', error);
    }
  };

  /**
   * Load filter options on mount.
   * 
   * Fetches available years, months, and sites from backend
   * to populate filter dropdowns when component mounts.
   * 
   * @effect
   * Runs once on mount.
   */
  useEffect(() => {
    loadFilterOptions();
  }, [selectedDataType]);

  useEffect(() => {
    loadPreviewData();
  }, [selectedYear, selectedMonth, selectedPeriod, selectedGenba, selectedDataType]);

  /**
   * Auto-dismiss message after 5 seconds.
   * 
   * Automatically clears download messages (success or error) after 5 seconds.
   * Provides cleanup function to clear timer on unmount or message change.
   * 
   * @effect
   * Runs when downloadMessage changes.
   */
  useEffect(() => {
    if (downloadMessage) {
      const timer = setTimeout(() => {
        setDownloadMessage('');
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [downloadMessage]);

  /**
   * Handles CSV download operation.
   * 
   * Validates that at least one filter is selected, then converts preview data
   * to CSV format and triggers browser download.
   * Displays success or error message after download.
   * 
   * Filter validation:
   * - At least one of: year, month, or site must be selected
   * 
   * CSV generation:
   * - Converts previewData to CSV format using convertToCSV
   * - Includes headers if includeHeaders is true
   * - Uses UTF-8 BOM (\uFEFF) for proper Japanese character encoding
   * 
   * File download:
   * - Creates Blob with CSV content and UTF-8 encoding
   * - Generates filename based on filters: {genbaName}_{year}_{month}.csv
   * - Triggers browser download using temporary link element
   * 
   * @example
   * ```typescript
   * // Download CSV for January 2024, site "現場A"
   * selectedYear = '2024';
   * selectedMonth = '1';
   * selectedGenba = '現場A';
   * await handleDownload();
   * // Downloads: "現場A_2024_1.csv"
   * ```
   */
  const handleDownload = async () => {
    /**
     * Check if at least one filter is selected.
     * Validates that user has selected at least year, month, or site.
     */
    if (!hasAnyFilter) {
      setDownloadMessage('少なくとも1つのフィルターを選択してください。');
      return;
    }

    setIsDownloading(true);
    setDownloadMessage('');

    try {
      /**
       * Convert preview data to CSV format.
       * Uses convertToCSV function to format data with headers.
       */
      const csvContent = selectedDataType === 'bonus'
        ? constructionBonusRowsToCsv(previewData as ConstructionBonusDataRow[], includeHeaders)
        : convertToCSV(previewData, includeHeaders);
      
      /**
       * Create and download file with proper encoding for Japanese characters.
       * Uses UTF-8 BOM (\uFEFF) to ensure proper display in Excel and other CSV readers.
       */
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      
      const genbaName = selectedGenba ? selectedGenba.replace(/\s+/g, '') : 'all';
      const year = selectedYear || 'all';
      const month = selectedMonth || 'all';
      const downloadName = selectedDataType === 'bonus'
        ? constructionBonusDownloadFilename(selectedGenba, selectedYear, selectedPeriod)
        : `${genbaName}_${year}_${month}.csv`;
      link.setAttribute('download', downloadName);
      link.style.visibility = 'hidden';
      
      /**
       * Trigger download by programmatically clicking link.
       * Temporarily appends link to body, clicks it, then removes it.
       */
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      /**
       * Build success message with selected filters and record count.
       * Includes time label (year/month), site name if selected, and record count.
       */
      let timeLabel = '';
      if (selectedYear) {
        const periodPart = selectedDataType === 'bonus' ? selectedPeriod : selectedMonth;
        if (periodPart) {
          timeLabel = `${selectedYear}年${periodPart}${selectedDataType === 'bonus' ? '期' : '月'}`;
        } else {
          timeLabel = `${selectedYear}年`;
        }
      }
      
      const filterLabel = selectedGenba ? ` (${selectedGenba})` : '';
      const recordCount = previewData.length;
      
      /**
       * Construct final success message with time label, site name, and record count.
       */
      const kindLabel = selectedDataType === 'bonus' ? '賞与' : '給与';
      const finalMessage = timeLabel 
        ? `建設作業員${kindLabel}データのダウンロードが完了しました。${timeLabel}${filterLabel} (${recordCount}件)`
        : `建設作業員${kindLabel}データのダウンロードが完了しました。${filterLabel} (${recordCount}件)`;
      
      setDownloadMessage(finalMessage);

    } catch (error: any) {
      /**
       * Handle download errors with user-friendly message.
       */
      setDownloadMessage('ダウンロード中にエラーが発生しました: ' + error.message);
    } finally {
      setIsDownloading(false);
    }
  };

  /**
   * Converts data array to CSV format string.
   * 
   * Matches the exact column structure from sample CSV files.
   * All values are quoted to match sample format.
   * Includes headers if includeHeaders is true.
   * 
   * Column structure:
   * - Employee info: 名前 (name), 出勤日数 (attendance days)
   * - Wage info: 賃金総額 (total wage), 基本給 (base salary), etc.
   * - Deductions: 健康保険 (health insurance), 雇用保険 (employment insurance), etc.
   * - Payments: 振込金額 (transfer amount), etc.
   * - Daily attendance: 1日 to 31日 (day1 to day31)
   * 
   * @param data - Array of construction worker salary records
   * @param includeHeaders - Whether to include CSV header row (default: true)
   * @returns CSV format string
   * 
   * @example
   * ```typescript
   * const csv = convertToCSV([{employeeName: 'John', totalWage: 100000}], true);
   * // Returns: "名前","賃金総額",...\n"John","100000",...
   * ```
   */
  const convertToCSV = (data: any[], includeHeaders: boolean): string => {
    /**
     * Return empty string if no data.
     */
    if (data.length === 0) return '';

    /**
     * Match the exact column structure from the sample CSV files.
     * Columns match the payslip structure with Japanese headers.
     */
    // New 67-column format (matches 柏崎原発作業所_2025_12 .txt)
    const headers = [
      '名前', '出勤日数', '賃金総額', '健康保険', '雇用保険', '厚生年金', '源泉税', '定額減税', '住民税',
      '法廷控除金計', '差引支給額', '食費', '諸式代', 'ガソリン代', '仮払金', '布団代', 'リース代',
      '協定控除金計', '控除額計', '差引支給額', 'その他支給', 'その他控除', '年末調整還付金', '振込金額',
      'その他支給内訳', 'その他控除内訳', '備考', '有給日数', '有給残数', '基本給', '出来高給', '現場手当', '時間外手当', '深夜手当', '休日手当', '所得税',
      '1日', '2日', '3日', '4日', '5日', '6日', '7日', '8日', '9日', '10日',
      '11日', '12日', '13日', '14日', '15日', '16日', '17日', '18日', '19日', '20日',
      '21日', '22日', '23日', '24日', '25日', '26日', '27日', '28日', '29日', '30日', '31日',
      '手当', '時間外', '深夜', '休日', 'その他手当'
    ];

    const csvRows: string[] = [];

    if (includeHeaders) {
      /**
       * Quote all headers to match sample format.
       * Headers are always quoted in the sample CSV files.
       */
      const quotedHeaders = headers.map(header => `"${header}"`);
      csvRows.push(quotedHeaders.join(','));
    }

    /**
     * Convert each data item to CSV row.
     * All values are quoted and default to 0 or empty string if undefined.
     */
    data.forEach(item => {
      const row = [
        `"${item.employeeName || ''}"`, // Employee name in quotes
        `"${item.attendanceDays || 0}"`, // Attendance days
        `"${item.totalWage || 0}"`, // Total wage
        `"${item.healthInsurance || 0}"`, // Health insurance
        `"${item.employmentInsurance || 0}"`, // Employment insurance
        `"${item.pensionInsurance || 0}"`, // Pension insurance
        `"${item.incomeTax || 0}"`, // Income tax
        `"${item.fixedReduction || 0}"`, // Fixed reduction
        `"${item.residentTax || 0}"`, // Resident tax
        `"${item.legalDeductionTotal || 0}"`, // Legal deduction total
        `"${item.deductedPayment1 || 0}"`, // Deducted payment 1
        `"${item.mealCost || 0}"`, // Meal cost
        `"${item.miscellaneousExpenses || 0}"`, // Miscellaneous expenses
        `"${item.gasolineCost || 0}"`, // Gasoline cost
        `"${item.advancePayment || 0}"`, // Advance payment
        `"${item.beddingCost || 0}"`, // Bedding cost
        `"${item.leaseCost || 0}"`, // Lease cost
        `"${item.agreementDeductionTotal || 0}"`, // Agreement deduction total
        `"${item.totalDeductions || 0}"`, // Total deductions
        `"${item.deductedPayment2 || 0}"`, // Deducted payment 2
        `"${item.otherPayment ?? 0}"`, // その他支給
        `"${item.otherDeduction ?? 0}"`, // その他控除
        `"${item.yearEndRefund ?? 0}"`, // 年末調整還付金
        `"${item.transferAmount || 0}"`, // Transfer amount
        `"${item.otherPaymentBreakdown ?? ''}"`, // その他支給内訳
        `"${item.otherDeductionBreakdown ?? ''}"`, // その他控除内訳
        `"${item.remarks ?? ''}"`, // 備考
        `"${item.paidLeaveDays || 0}"`, // Paid leave days
        `"${item.remainingPaidLeave || 0}"`, // Remaining paid leave
        `"${item.overtimeHours || 0}"`, // Overtime hours
        `"${item.baseSalary || 0}"`, // Base salary
        `"${item.pieceRatePay || 0}"`, // Piece rate pay
        `"${item.siteAllowance || 0}"`, // Site allowance
        `"${item.overtimeAllowance || 0}"`, // Overtime allowance
        `"${item.nightShiftAllowance || 0}"`, // Night shift allowance
        `"${item.holidayAllowance || 0}"`, // Holiday allowance
        `"${item.incomeTax2 || 0}"`, // Income tax 2
        /**
         * Daily attendance data (1日 to 31日) - ensure these are properly mapped.
         * Each day's value is quoted, defaults to empty string if undefined.
         */
        `"${item.day1 || ''}"`, `"${item.day2 || ''}"`, `"${item.day3 || ''}"`, `"${item.day4 || ''}"`, `"${item.day5 || ''}"`,
        `"${item.day6 || ''}"`, `"${item.day7 || ''}"`, `"${item.day8 || ''}"`, `"${item.day9 || ''}"`, `"${item.day10 || ''}"`,
        `"${item.day11 || ''}"`, `"${item.day12 || ''}"`, `"${item.day13 || ''}"`, `"${item.day14 || ''}"`, `"${item.day15 || ''}"`,
        `"${item.day16 || ''}"`, `"${item.day17 || ''}"`, `"${item.day18 || ''}"`, `"${item.day19 || ''}"`, `"${item.day20 || ''}"`,
        `"${item.day21 || ''}"`, `"${item.day22 || ''}"`, `"${item.day23 || ''}"`, `"${item.day24 || ''}"`, `"${item.day25 || ''}"`,
        `"${item.day26 || ''}"`, `"${item.day27 || ''}"`, `"${item.day28 || ''}"`, `"${item.day29 || ''}"`, `"${item.day30 || ''}"`, `"${item.day31 || ''}"`,
        `"${item.allowance ?? ''}"`,
        `"${item.overtimeHours ?? ''}"`,
        `"${item.nightShiftHours ?? ''}"`,
        `"${item.holidayHours ?? ''}"`,
        `"${item.otherAllowance ?? 0}"`
      ];
      csvRows.push(row.join(','));
    });

    /**
     * Join all rows with newlines to create CSV string.
     */
    return csvRows.join('\n');
  };

  /**
   * Formats a number as Japanese currency format.
   * 
   * Uses Intl.NumberFormat with 'ja-JP' locale and JPY currency.
   * Formats numbers with currency symbol and appropriate thousands separators.
   * 
   * @param amount - Number to format
   * @returns Formatted string (e.g., "¥123,456")
   * 
   * @example
   * ```typescript
   * formatCurrency(123456); // Returns: "¥123,456"
   * ```
   */
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
      minimumFractionDigits: 0
    }).format(amount);
  };

  /**
   * Formats a number with Japanese locale formatting.
   * 
   * Uses Intl.NumberFormat with 'ja-JP' locale to format numbers
   * with appropriate thousands separators.
   * 
   * @param num - Number to format
   * @returns Formatted string (e.g., "1,234,567")
   * 
   * @example
   * ```typescript
   * formatNumber(1234567); // Returns: "1,234,567"
   * ```
   */
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('ja-JP').format(num);
  };

  /**
   * Check if any filter is selected.
   * 
   * Used to enable/disable download button.
   * At least one filter must be selected to download.
   * 
   * @returns true if any filter (year, month, or site) is selected
   */
  const hasAnyFilter = selectedYear !== '' || selectedMonth !== '' || selectedPeriod !== '' || selectedGenba !== '';

  return (
    <div className="csv-download-container">
      {/* Download Message Display - Shows success/error messages */}
      {downloadMessage && (
        <div className={`message ${downloadMessage.includes('失敗') || downloadMessage.includes('エラー') ? 'error' : 'success'}`}>
          {/* Message icon - ❌ for errors, ✅ for success */}
          <span className="message-icon">
            {downloadMessage.includes('失敗') || downloadMessage.includes('エラー') ? '❌' : '✅'}
          </span>
          {/* Message text */}
          <span className="message-text">{downloadMessage}</span>
          {/* Close button - Dismisses message */}
          <button 
            className="message-close-btn"
            onClick={() => setDownloadMessage('')}
            aria-label="閉じる"
          >
            ×
          </button>
        </div>
      )}

      {/* Download Options Section */}
      <div className="download-options">
        <div className="option-card">
          {/* Instructions - At least one filter is required */}
          <p>
            {selectedDataType === 'bonus'
              ? '選択した条件（少なくとも1つのフィルターが必要）の建設作業員賞与データをCSV形式でダウンロードします'
              : '選択した条件（少なくとも1つのフィルターが必要）の建設作業員給与データをCSV形式でダウンロードします'}
          </p>
          
          <div className="selection-group">
            {/* Left Column - Basic Settings */}
            <div className="selection-column">
              <h4>基本設定</h4>

              <div className="select-wrapper">
                <label htmlFor="data-type-select">データ種別:</label>
                <select
                  id="data-type-select"
                  value={selectedDataType}
                  onChange={(e) => {
                    const next = e.target.value as 'salary' | 'bonus';
                    setSelectedDataType(next);
                    setSelectedMonth('');
                    setSelectedPeriod('');
                    setSelectedYear('');
                    setSelectedGenba('');
                    setDownloadMessage('');
                    setPreviewData([]);
                  }}
                  disabled={isDownloading}
                >
                  <option value="salary">給与データ</option>
                  <option value="bonus">賞与データ</option>
                </select>
              </div>
              
              {/* Year Selection - Dynamically loaded from backend */}
              <div className="select-wrapper">
                <label htmlFor="year-select">年 (オプション):</label>
                <select
                  id="year-select"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  disabled={isDownloading}
                >
                  <option value="">年を選択</option>
                  {/* Generate year options from available years */}
                  {availableYears.map(year => (
                    <option key={year} value={year}>{year}年</option>
                  ))}
                </select>
              </div>

              {selectedDataType === 'salary' ? (
                <div className="select-wrapper">
                  <label htmlFor="month-select">月 (オプション):</label>
                  <select
                    id="month-select"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    disabled={isDownloading}
                  >
                    <option value="">月を選択</option>
                    {availableMonths.map(month => (
                      <option key={month} value={month}>{month}月</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="select-wrapper">
                  <label htmlFor="period-select">期 (オプション):</label>
                  <select
                    id="period-select"
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                    disabled={isDownloading}
                  >
                    <option value="">期を選択</option>
                    {availablePeriods.map(period => (
                      <option key={period} value={period}>{period}月</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Right Column - Filter Settings */}
            <div className="selection-column">
              <h4>フィルター設定</h4>
              
              {/* Site (現場) Selection - Dynamically loaded from backend, unique to construction workers */}
              <div className="select-wrapper">
                <label htmlFor="genba-select">現場 (オプション):</label>
                <select
                  id="genba-select"
                  value={selectedGenba}
                  onChange={(e) => setSelectedGenba(e.target.value)}
                  disabled={isDownloading}
                >
                  <option value="">全現場</option>
                  {/* Generate site options from available sites (現場) */}
                  {availableGenbas.map(genba => (
                    <option key={genba} value={genba}>{genba}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Download Button - Disabled when downloading or no filters selected */}
          <button
            className="download-btn primary"
            onClick={handleDownload}
            disabled={isDownloading || !hasAnyFilter}
          >
            {isDownloading ? 'ダウンロード中...' : 'CSVダウンロード'}
          </button>
        </div>
      </div>

      {/* Action Buttons Section */}
      <div className="action-buttons">
        {/* Clear Selection Button - Resets all filters and messages */}
        <button
          className="clear-btn"
          onClick={() => {
            setSelectedYear('');
            setSelectedMonth('');
            setSelectedPeriod('');
            setSelectedGenba('');
            setDownloadMessage('');
          }}
          disabled={isDownloading}
        >
          選択クリア
        </button>
      </div>
    </div>
  );
};